import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { ChevronDown, ChevronRight, Download, Loader2, PhoneCall, Users } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const card = { background: '#1A2433', border: '1px solid #2D3748' };
const muted = { color: '#64748B' };
const h = (v) => `${Number(v || 0).toFixed(2)}`;
const money = (v) => v == null ? '—' : `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const Chip = ({ children, color = '#00C896', testid }) => (
  <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold" style={{ background: `${color}22`, color }} data-testid={testid}>{children}</span>
);

function Summary({ s, daily }) {
  const items = [
    ['Hours worked', h(s.worked), null], ['Regular', h(s.regular), '#E2E8F0'], ['Overtime', h(s.overtime), '#FFA000'],
    ['Call-out top-up', h(s.topup), '#F472B6'], ['Payroll hours', h(s.payroll), '#00C896'],
    ['On jobs', h(s.job_hours), null], ['Project only', h(s.project_only_hours), null], ['No project', h(s.unassigned_hours), s.unassigned_hours > 0 ? '#EF4444' : null],
  ];
  return (
    <div className="grid grid-cols-4 lg:grid-cols-8 gap-2" data-testid="hr-summary">
      {items.map(([l, v, c]) => (
        <div key={l} className="rounded-lg p-2.5" style={card}>
          <p className="text-[10px] uppercase tracking-wider" style={muted}>{l}</p>
          <p className="text-base font-semibold" style={{ color: c || '#fff' }}>{v} h</p>
        </div>
      ))}
      <p className="col-span-4 lg:col-span-8 text-[11px]" style={muted}>
        {s.workers} workers ({s.service_workers} service · {s.hourly_workers} hourly) · {s.callouts} call-outs · OT after {daily} h/day or the weekly threshold, whichever is greater · top-up hours are regular pay{s.open_entries ? ` · ${s.open_entries} open session(s) still running` : ''}
      </p>
    </div>
  );
}

function DayRow({ d }) {
  return (
    <div className="rounded-lg overflow-hidden" style={{ border: '1px solid #2D3748' }} data-testid={`hr-day-${d.date}`}>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-1.5 text-xs" style={{ background: '#0F1923' }}>
        <span className="font-medium text-white w-24">{d.date}</span>
        <span style={muted}>worked <b className="text-white">{h(d.worked)}</b></span>
        <span style={muted}>regular <b className="text-white">{h(d.regular)}</b></span>
        {d.overtime > 0 && <span style={muted}>OT <b style={{ color: '#FFA000' }}>{h(d.overtime)}</b></span>}
        {d.topup > 0 && <span style={muted}>top-up <b style={{ color: '#F472B6' }}>{h(d.topup)}</b></span>}
        <span className="ml-auto" style={muted}>payroll <b style={{ color: '#00C896' }}>{h(d.payroll)}</b></span>
      </div>
      <table className="w-full text-xs">
        <tbody>
          {d.entries.map((e) => (
            <tr key={e.entry_id} style={{ borderTop: '1px solid #1F2D3F', color: '#E2E8F0' }} data-testid={`hr-entry-${e.entry_id}`}>
              <td className="px-3 py-1.5 w-28 font-mono">{e.clock_in_local} – {e.clock_out_local || <span style={{ color: '#FFA000' }}>open</span>}</td>
              <td className="px-2 py-1.5 w-16 text-right font-mono">{h(e.hours)}</td>
              <td className="px-2 py-1.5">
                {e.job_number || e.job_title ? <span>{e.job_number ? `${e.job_number} · ` : ''}{e.job_title}{e.customer_name ? <span style={muted}> · {e.customer_name}</span> : null}</span>
                  : <span style={muted}>{e.project_name ? 'Project time (no job)' : 'No project or job'}</span>}
                {e.project_name && <div className="text-[10px]" style={muted}>{e.project_number ? `${e.project_number} · ` : ''}{e.project_name}</div>}
              </td>
              <td className="px-2 py-1.5 text-right space-x-1 whitespace-nowrap">
                {e.callout && <Chip color="#F472B6" testid={`hr-callout-${e.entry_id}`}><PhoneCall className="inline w-3 h-3 mr-0.5" />call-out{e.callout_topup ? ` +${h(e.callout_topup)}` : ''}</Chip>}
                {!e.callout && e.in_window && <Chip color="#FFA000" testid={`hr-inwindow-${e.entry_id}`}>after-hours, not tagged</Chip>}
                {e.offline && <Chip color="#FFA000">offline</Chip>}
                {e.edited && <Chip color="#1E88E5">edited</Chip>}
                {e.tagged_by && <Chip color="#94A3B8">tagged by {e.tagged_by}</Chip>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function WorkerCard({ w }) {
  const [open, setOpen] = useState(false);
  const t = w.totals;
  return (
    <div className="rounded-xl overflow-hidden" style={card} data-testid={`hr-worker-${w.worker_id}`}>
      <button onClick={() => setOpen((o) => !o)} className="w-full flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2.5 text-left" data-testid={`hr-worker-toggle-${w.worker_id}`}>
        {open ? <ChevronDown className="w-4 h-4" style={muted} /> : <ChevronRight className="w-4 h-4" style={muted} />}
        <span className="text-sm font-medium text-white">{w.worker_name}</span>
        <Chip color={w.pay_basis === 'service' ? '#1E88E5' : '#00C896'} testid={`hr-pay-basis-${w.worker_id}`}>{w.pay_basis === 'service' ? 'Service · by job' : 'Hourly · by project'}</Chip>
        {w.work_region && <Chip color="#94A3B8">{w.work_region} · min {w.callout_min_hours}h</Chip>}
        <span className="ml-auto flex flex-wrap gap-x-3 text-xs" style={muted}>
          <span>worked <b className="text-white">{h(t.worked)}</b></span>
          <span>reg <b className="text-white">{h(t.regular)}</b></span>
          <span>OT <b style={{ color: t.overtime > 0 ? '#FFA000' : '#E2E8F0' }}>{h(t.overtime)}</b></span>
          <span>top-up <b style={{ color: t.topup > 0 ? '#F472B6' : '#E2E8F0' }}>{h(t.topup)}</b></span>
          <span>payroll <b style={{ color: '#00C896' }}>{h(t.payroll)}</b></span>
          {w.gross_estimate != null && <span>≈ <b className="text-white">{money(w.gross_estimate)}</b></span>}
        </span>
      </button>
      {open && (
        <div className="px-3 pb-3 space-y-2">
          <div className="flex flex-wrap gap-2 text-[11px]" style={muted}>
            <span>On jobs: <b className="text-white">{h(t.job_hours)} h</b></span>
            <span>Project only: <b className="text-white">{h(t.project_only_hours)} h</b></span>
            {t.unassigned_hours > 0 && <span style={{ color: '#EF4444' }}>No project/job: <b>{h(t.unassigned_hours)} h</b></span>}
            <span>Call-outs: <b className="text-white">{t.callouts}</b> ({h(t.callout_worked)} h worked)</span>
            <span>OT policy: {w.overtime_policy} · {w.weekly_threshold} h/week</span>
          </div>
          {w.days.map((d) => <DayRow key={d.date} d={d} />)}
          <div className="grid md:grid-cols-2 gap-2 pt-1">
            <div className="rounded-lg p-2" style={{ background: '#0F1923' }}>
              <p className="text-[10px] uppercase tracking-wider mb-1" style={muted}>Hours by project</p>
              {w.by_project.map((p, i) => <div key={i} className="flex justify-between text-xs text-slate-200"><span>{p.project_number ? `${p.project_number} · ` : ''}{p.name}</span><span className="font-mono">{h(p.hours)}</span></div>)}
            </div>
            <div className="rounded-lg p-2" style={{ background: '#0F1923' }}>
              <p className="text-[10px] uppercase tracking-wider mb-1" style={muted}>Hours by job</p>
              {w.by_job.length === 0 ? <p className="text-xs" style={muted}>No job time</p> : w.by_job.map((j) => <div key={j.job_id} className="flex justify-between text-xs text-slate-200"><span>{j.job_number ? `${j.job_number} · ` : ''}{j.title}</span><span className="font-mono">{h(j.hours)}</span></div>)}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function HoursReviewPane({ token, startDate, endDate }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [basis, setBasis] = useState('all');
  const [q, setQ] = useState('');
  const auth = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const load = useCallback(async () => {
    if (!startDate || !endDate) return;
    setLoading(true);
    try {
      const { data: d } = await axios.get(`${API}/reports/hours-review`, { params: { start_date: startDate, end_date: endDate }, ...auth });
      setData(d);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load the hours review');
    } finally { setLoading(false); }
  }, [startDate, endDate, auth]);
  useEffect(() => { load(); }, [load]);

  const exportCsv = async () => {
    const res = await axios.get(`${API}/reports/hours-review.csv`, { params: { start_date: startDate, end_date: endDate }, responseType: 'blob', ...auth });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement('a'); a.href = url; a.download = `hours-review-${startDate}-to-${endDate}.csv`; a.click(); URL.revokeObjectURL(url);
  };

  const workers = (data?.workers || []).filter((w) => (basis === 'all' || w.pay_basis === basis) && (!q || (w.worker_name || '').toLowerCase().includes(q.toLowerCase())));

  return (
    <div className="rounded-xl overflow-hidden" style={{ background: '#0F1923', border: '1px solid #2D3748' }} data-testid="hours-review-pane">
      <div className="flex flex-wrap items-center gap-3 p-3 border-b" style={{ borderColor: '#2D3748' }}>
        <Users className="w-4 h-4" style={{ color: '#00C896' }} />
        <div className="text-sm font-medium text-white">Hours Review — what is what before payroll</div>
        <span className="text-xs" style={muted}>{startDate} → {endDate}</span>
        <div className="flex items-center gap-1 ml-2">
          {[['all', 'All'], ['service', 'Service'], ['hourly', 'Hourly']].map(([v, l]) => (
            <button key={v} onClick={() => setBasis(v)} className="px-2.5 py-1 rounded-lg text-xs" style={{ background: basis === v ? '#1E88E5' : '#1A2433', color: '#fff' }} data-testid={`hr-basis-${v}`}>{l}</button>
          ))}
        </div>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find worker…" className="px-2 py-1.5 rounded-lg text-xs" style={{ background: '#1A2433', color: '#E2E8F0', border: '1px solid #2D3748' }} data-testid="hr-search" />
        {loading && <Loader2 className="w-4 h-4 animate-spin" style={muted} />}
        <button onClick={exportCsv} disabled={!data} className="ml-auto flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium disabled:opacity-50" style={{ background: '#00C896', color: '#0B1321' }} data-testid="hr-export-csv-btn">
          <Download className="w-3.5 h-3.5" /> Export CSV
        </button>
      </div>
      {data ? (
        <div className="p-3 space-y-3">
          <Summary s={data.summary} daily={data.daily_threshold} />
          {workers.length === 0 ? <p className="text-xs p-4 text-center" style={muted}>No hours in this range.</p> : workers.map((w) => <WorkerCard key={w.worker_id} w={w} />)}
        </div>
      ) : <div className="p-8 text-center text-xs" style={muted}>{loading ? 'Loading…' : 'Pick a pay period above'}</div>}
    </div>
  );
}
